import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Box,
  Snackbar,
  Typography,
  List,
  ListItem,
  IconButton,
  ListItemButton,
  ListItemIcon,
  Button,
  ListItemText,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { autorun } from 'mobx';
import DeleteIcon from '@mui/icons-material/Delete';
import md5 from 'md5';
import { getFireSafetyStructure } from '../csvImport';
import { updateFireSafety } from '../firebaseConnection/database';
import AddFireDialog from '../components/dialog/AddFireDialog';
import { FireSafetyEntryWithImage } from '../types/transport';
import { useStore } from '../store/context';
import {
  addCategoryImage,
  deleteCategoryImage,
  fireSafetyCategoryImage,
} from '../firebaseConnection/storage';
import Dropzone from '../hooks/Dropzone';

const FireSafetyPage = observer((): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [openAddCategory, setOpenAddCategory] = useState(false);
  const [currentEntry, setCurrentEntry] = useState<
    FireSafetyEntryWithImage | undefined
  >();
  const [categories, setCategories] = useState<FireSafetyEntryWithImage[]>([]);
  const { fireSafetyStore } = useStore();

  useEffect(() => {
    autorun(() => {
      (async function getCategories() {
        if (fireSafetyStore?.allEntries) {
          const allEntries = await Promise.all(
            fireSafetyStore?.allEntries.map(async entry => {
              try {
                const imageFileName = await fireSafetyCategoryImage(
                  entry.imageFileName,
                );
                return { ...entry, localImageUrl: imageFileName };
              } catch (e) {
                return { ...entry, localImageUrl: '' };
              }
            }),
          );
          setCategories(allEntries);
        }
      })();
    });
  }, [fireSafetyStore]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles[0]) {
      try {
        const firebaseData = await getFireSafetyStructure(acceptedFiles[0]);
        delete firebaseData.undefined;
        delete firebaseData[''];
        console.log(firebaseData);
        await updateFireSafety(firebaseData);
        setMessage('Success!');
        setOpen(true);
      } catch (e) {
        setMessage(`Error: ${e}`);
        setOpen(true);
      }
      setOpen(true);
    }
  }, []);

  const addCategory = async (
    entry: FireSafetyEntryWithImage,
    image: File,
    newTitle: string,
  ) => {
    if (
      categories &&
      categories.filter(elem => elem.titleMd5 !== entry.titleMd5).length === 0
    ) {
      categories.push(entry);
    }
    if (image.size > 0) {
      try {
        await addCategoryImage(entry.imageFileName, image);
      } catch (e) {
        setMessage(`Error: ${e}`);
        setOpen(true);
      }
    }
    const uploadEntry = entry;
    delete uploadEntry.localImageUrl;
    fireSafetyStore?.updateFireCategory(entry.titleMd5, uploadEntry);

    if (newTitle !== entry.title) {
      fireSafetyStore?.updateFireCategory(md5(newTitle), null);
    }
    setOpenAddCategory(false);
  };

  const openFireEntry = (entry: FireSafetyEntryWithImage) => {
    setCurrentEntry(entry);
    setOpenAddCategory(true);
  };

  const deleteFireSafetyItem = async (entry: FireSafetyEntryWithImage) => {
    try {
      await deleteCategoryImage(entry.imageFileName);
      fireSafetyStore?.updateFireCategory(entry.titleMd5, null);
    } catch (e) {
      setMessage(`Error: ${e}`);
      setOpen(true);
    }
  };

  return (
    <>
      <Container sx={{ width: `calc(100% - ${240}px)`, ml: `${240}px` }}>
        <Box
          component="main"
          sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}
        >
          <Typography variant="h4">Brandschutz</Typography>
          <Dropzone drop={csvFile => onDrop(csvFile)} />
          <Button
            onClick={() => {
              setCurrentEntry(undefined);
              setOpenAddCategory(true);
            }}
          >
            Kategorie hinzufügen
          </Button>
          <hr />
          <Box sx={{ maxWidth: 300 }}>
            <List dense>
              {categories?.map(entry => (
                <ListItem
                  key={entry.titleMd5}
                  secondaryAction={
                    <IconButton
                      onClick={() => deleteFireSafetyItem(entry)}
                      edge="end"
                      aria-label="delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemButton onClick={() => openFireEntry(entry)}>
                    <ListItemIcon>
                      {entry.localImageUrl && (
                        <img
                          src={entry.localImageUrl}
                          alt="kategorie"
                          style={{ maxWidth: 100 }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={entry.title} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          message={message}
        />
        <AddFireDialog
          open={openAddCategory}
          fireSafetyItem={currentEntry}
          onSave={(entry, image, title) => addCategory(entry, image, title)}
          onClose={() => setOpenAddCategory(false)}
        />
      </Container>
    </>
  );
});

export default FireSafetyPage;
